import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { connect } from "react-redux";
import ClinicalEvidencesTable from "../../../../shared/CIVIC/ClinicalEvidencesTable";
import Chart from "../../../../../components/atoms/charts/Chart";
import withErrorGuard from "../../../../../HOC/withErrorGuard";
import {
  civicTitleSelector,
  getCivic,
  civicPublicationsSelector,
  civicLabelsSelector,
} from "../../../../../state/selectors/Civic.selectors";
import AssertionDetailsTable from "./AssertionTable/AssertionDetailsTable";
import ComponentPanel from "../../../../../components/atoms/ComponentPanel/ComponentPanel";
import VariantEvidenceScore from "./VariantEvidenceScore";
import Gene from "./Gene";
import VariantGroupsInformation from "./VariantGroupsInformation";
import {
  getVarsomeHost,
  getGenomeName,
} from "../../../../../state/selectors/general.selectors";
import CivicUrl from "./CivicUrl";
import { getFormattedData } from "./helpers";
import Transcript from "./Transcript";
import Variant from "./Variant";
import VariantSummary from "./VariantSummary";
import MolecularProfilesTable from "./MolecularProfilesTable/MolecularProfilesTable";
import MolecularProfileUrl from "./MolecularProfileUrl";
import HelperText from "../../../../../components/atoms/HelperText";

const StyledChartsGroup = styled.div`
  display: flex;
  overflow-y: auto;
  justify-content: space-around;
  margin: 0 0 2rem 0;

  @media (max-width: 1200px) {
    justify-content: initial;
  }
`;

const CivicResults = ({
  title,
  civic,
  publications,
  labels,
  varsomeHost,
  refGenome,
}) => {
  if (labels && civic) {
    const civicObject = civic[0];
    const { version } = civicObject || null;
    const formattedData = getFormattedData(civicObject);
    const civicObjectMainItem = civicObject?.items[0];
    const {
      variant,
      civic_variant_evidence_score: civicEvidenceScore,
      variant_group: variantGroups,
      variant_civic_url: variantUrl,
      assertion_details: assertionDetails,
      gene,
      representative_transcript: transcript,
      variant_origin: variantOrigin,
      variant_summary: variantSummary,
      molecular_profiles: molecularProfiles,
      molecular_profile_civic_url: molecularProfileUrl,
    } = civicObjectMainItem;
    const { label: variantLabel } = labels?.variant || null;
    const { label: variantEvidenceScoreLabel } =
      labels?.civic_variant_evidence_score || null;
    const { label: transcriptLabel } =
      labels?.representative_transcript || null;
    const { label: geneLabel } = labels?.gene;
    const content = (
      <div className="varsome-inner-container">
        {Array.isArray(civicObject.items) && civicObject.items.length > 0 && (
          <React.Fragment>
            <div className="varsome-horiz-flex">
              {variantSummary && (
                <VariantSummary
                  variantSummary={variantSummary}
                  label={labels.variant_summary.label}
                />
              )}
              {gene && (
                <Gene gene={gene} label={geneLabel} varsomeHost={varsomeHost} />
              )}
              {variant && (
                <Variant
                  label={variantLabel}
                  variantUrl={variantUrl}
                  variant={variant}
                  variantOrigin={variantOrigin}
                />
              )}
              {molecularProfileUrl && (
                <MolecularProfileUrl
                  molecularProfileUrl={molecularProfileUrl}
                  gene={gene}
                  variant={variant}
                />
              )}
              {transcript && (
                <Transcript
                  label={transcriptLabel}
                  transcript={transcript}
                  varsomeHost={varsomeHost}
                  refGenome={refGenome}
                />
              )}
              {civicEvidenceScore && (
                <VariantEvidenceScore
                  label={variantEvidenceScoreLabel}
                  score={civicEvidenceScore}
                />
              )}
            </div>
            <div className="varsome-horiz-flex">
              {variantGroups && (
                <VariantGroupsInformation variantGroups={variantGroups} />
              )}
            </div>
          </React.Fragment>
        )}
        <StyledChartsGroup>
          <Chart data={formattedData} title="Disease Chart" />
        </StyledChartsGroup>
        <AssertionDetailsTable
          assertionDetails={assertionDetails}
          variant={variant}
          variantUrl={variantUrl}
        />
        <MolecularProfilesTable molecularProfiles={molecularProfiles} />
        <ClinicalEvidencesTable
          result={civicObject}
          pub_med_articles={publications}
        />
      </div>
    );

    return (
      <div className="varsome-result-fixed">
        <div className="varsome-container varsome-container__basic">
          <ComponentPanel
            title={title}
            version={version}
            url="https://civicdb.org/welcome"
            titleDetailsRight={<CivicUrl civicUrl={variantUrl} />}
            helperPopup={<HelperText fieldDescriptionKey="CIViC" />}
            content={content}
          />
        </div>
      </div>
    );
  }

  return null;
};

CivicResults.propTypes = {
  title: PropTypes.string,
  civic: PropTypes.arrayOf(
    PropTypes.shape({
      version: PropTypes.string,
      // eslint-disable-next-line react/forbid-prop-types
      items: PropTypes.arrayOf(PropTypes.any),
    })
  ),
  // eslint-disable-next-line react/forbid-prop-types
  publications: PropTypes.arrayOf(PropTypes.any),
  labels: PropTypes.shape({
    variant_summary: PropTypes.shape({
      label: PropTypes.string,
    }),
    variant: PropTypes.shape({
      label: PropTypes.string,
    }),
    civic_variant_evidence_score: PropTypes.shape({
      label: PropTypes.string,
    }),
    representative_transcript: PropTypes.shape({
      label: PropTypes.string,
    }),
    gene: PropTypes.shape({
      label: PropTypes.string,
    }),
  }),
  varsomeHost: PropTypes.string,
  refGenome: PropTypes.string,
};

CivicResults.defaultProps = {
  title: null,
  civic: null,
  publications: null,
  labels: null,
  varsomeHost: null,
  refGenome: null,
};

const mapStateToProps = (state) => ({
  title: civicTitleSelector(state),
  civic: getCivic(state),
  publications: civicPublicationsSelector(state),
  labels: civicLabelsSelector(state),
  varsomeHost: getVarsomeHost(state),
  refGenome: getGenomeName(state),
});

export default connect(
  mapStateToProps,
  {}
)(withErrorGuard(CivicResults, "WUSTL CIViC", "CIViC"));
